import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/client';
import { CameraPlus, CheckCircle, XCircle, X, UserPlus, Users, ProhibitInset } from '@phosphor-icons/react';
import * as faceapi from 'face-api.js';
import * as tf from '@tensorflow/tfjs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

function MarkAttendance() {
  const getLocalDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const navigate = useNavigate();
  const userRole = localStorage.getItem('role') || 'admin';
  const isFaceOnly = userRole === 'face_only';
  const [students, setStudents] = useState([]);
  const [faceStudents, setFaceStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState(isFaceOnly ? 'face' : 'manual');
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualBatch, setManualBatch] = useState('');
  const [manualSearch, setManualSearch] = useState('');
  const [manualDate, setManualDate] = useState(getLocalDate());
  const [markingManual, setMarkingManual] = useState(false);
  const [absentIds, setAbsentIds] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleCancel = () => {
    stopCamera();
    navigate('/');
  };

  useEffect(() => {
    fetchStudents();
    fetchBatches();
    fetchAbsences();
    loadModels();
    return () => {
      stopCamera();
    };
  }, []);

  const getCurrentMonth = () => {
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return months[new Date().getMonth()];
  };
  const getCurrentYear = () => new Date().getFullYear();

  const fetchAbsences = async () => {
    try {
      const response = await api.get(`/absences?month=${getCurrentMonth()}&year=${getCurrentYear()}`);
      setAbsentIds(response.data.map(a => a.student_id));
    } catch (error) {
      console.error('Failed to fetch absences');
    }
  };

  const loadModels = async () => {
    try {
      // Try WebGL first, fall back to CPU if unavailable
      try {
        await tf.setBackend('webgl');
        await tf.ready();
        console.log('Using WebGL backend');
      } catch (webglError) {
        console.warn('WebGL not available, falling back to CPU backend');
        await tf.setBackend('cpu');
        await tf.ready();
        console.log('Using CPU backend');
      }

      const MODEL_URL = `${process.env.REACT_APP_BACKEND_URL}/api/models`;
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setModelsLoaded(true);
    } catch (error) {
      console.error('Error loading models:', error);
      toast.error('Failed to load face detection models. Please try a different browser.');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => console.warn('Auto-play blocked:', e));
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Failed to access camera');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
    }
  };

  const fetchStudents = async () => {
    try {
      const [allRes, faceRes] = await Promise.all([
        api.get('/students'),
        api.get('/students/with-faces'),
      ]);
      setStudents(allRes.data.filter(s => s.is_active));
      setFaceStudents(faceRes.data);
    } catch (error) {
      toast.error('Failed to fetch students');
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch batches');
    }
  };

  const euclideanDistance = (a, b) => {
    return Math.sqrt(a.reduce((sum, val, i) => sum + Math.pow(val - b[i], 2), 0));
  };

  const recognizeFace = async () => {
    if (!videoRef.current || !modelsLoaded) return;

    setCapturing(true);
    setResult(null);

    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        setResult({ success: false, message: 'No face detected. Please ensure your face is clearly visible.' });
        setCapturing(false);
        return;
      }

      const capturedDescriptor = Array.from(detection.descriptor);
      
      const filteredStudents = selectedBatch
        ? faceStudents.filter(s => s.batch_id === selectedBatch)
        : faceStudents;

      if (filteredStudents.length === 0) {
        setResult({ success: false, message: 'No students registered in this batch.' });
        setCapturing(false);
        return;
      }

      let bestMatch = null;
      let minDistance = Infinity;

      for (const student of filteredStudents) {
        if (student.face_descriptor && student.face_descriptor.length > 0) {
          const distance = euclideanDistance(capturedDescriptor, student.face_descriptor);
          if (distance < minDistance) {
            minDistance = distance;
            bestMatch = student;
          }
        }
      }

      const threshold = 0.6;
      const confidenceScore = Math.max(0, 1 - minDistance);

      if (minDistance < threshold && bestMatch) {
        const today = getLocalDate();
        
        try {
          await api.post('/attendance', {
            student_id: bestMatch.id,
            student_name: bestMatch.name,
            batch_id: bestMatch.batch_id,
            confidence_score: confidenceScore,
            date: today,
          });
          
          setResult({
            success: true,
            message: `Welcome, ${bestMatch.name}!`,
            student: bestMatch,
            confidence: (confidenceScore * 100).toFixed(1),
          });
          toast.success(`Attendance marked for ${bestMatch.name}`);
        } catch (error) {
          if (error.response?.status === 400) {
            setResult({
              success: false,
              message: `${bestMatch.name} - Attendance already marked today`,
              student: bestMatch,
            });
            toast.warning('Attendance already marked for this student');
          } else {
            setResult({ success: false, message: 'Failed to mark attendance' });
            toast.error('Failed to mark attendance');
          }
        }
      } else {
        setResult({ success: false, message: 'Face not recognized. Please register first.' });
        toast.error('Face not recognized');
      }
    } catch (error) {
      console.error('Recognition error:', error);
      setResult({ success: false, message: 'An error occurred during recognition' });
      toast.error('Recognition failed');
    } finally {
      setCapturing(false);
    }
  };

  const handleManualAttendance = async (student) => {
    setMarkingManual(true);
    const today = getLocalDate();
    try {
      await api.post('/attendance', {
        student_id: student.id,
        student_name: student.name,
        batch_id: student.batch_id,
        confidence_score: 1.0,
        date: manualDate,
      });
      toast.success(`Attendance marked for ${student.name}`);
      setManualDialogOpen(false);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.warning(`${student.name} - Attendance already marked on ${manualDate}`);
      } else {
        toast.error('Failed to mark attendance');
      }
    } finally {
      setMarkingManual(false);
    }
  };

  const getManualFilteredStudents = () => {
    let filtered = students;
    if (manualBatch) {
      filtered = filtered.filter(s => s.batch_id === manualBatch);
    }
    if (manualSearch.trim()) {
      const q = manualSearch.toLowerCase();
      filtered = filtered.filter(s => s.name.toLowerCase().includes(q));
    }
    return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  const handleMarkAbsent = async (student) => {
    try {
      await api.post('/absences', {
        student_id: student.id,
        month: getCurrentMonth(),
        year: getCurrentYear(),
      });
      toast.success(`${student.name} marked absent for ${getCurrentMonth()}`);
      setAbsentIds(prev => [...prev, student.id]);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.warning(`${student.name} already marked absent for ${getCurrentMonth()}`);
      } else {
        toast.error('Failed to mark absent');
      }
    }
  };

  const handleRemoveAbsent = async (student) => {
    try {
      await api.delete(`/absences?student_id=${student.id}&month=${getCurrentMonth()}&year=${getCurrentYear()}`);
      toast.success(`${student.name} absence removed`);
      setAbsentIds(prev => prev.filter(id => id !== student.id));
    } catch (error) {
      toast.error('Failed to remove absence');
    }
  };

  useEffect(() => {
    if (mode === 'face' && modelsLoaded) {
      startCamera();
    }
    if (mode === 'manual') {
      stopCamera();
    }
  }, [mode, modelsLoaded]);

  useEffect(() => {
    if (modelsLoaded && mode === 'face') {
      const interval = setInterval(async () => {
        if (!capturing && videoRef.current && videoRef.current.srcObject) {
          try {
            const detection = await faceapi
              .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
              .withFaceLandmarks();

            if (detection && canvasRef.current) {
              const canvas = canvasRef.current;
              const displaySize = { width: videoRef.current.width, height: videoRef.current.height };
              faceapi.matchDimensions(canvas, displaySize);
              const resizedDetections = faceapi.resizeResults(detection, displaySize);
              canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
              faceapi.draw.drawDetections(canvas, resizedDetections);
            }
          } catch (e) {
            // Video element may not be ready
          }
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [modelsLoaded, capturing, mode]);

  const getBatchName = (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.name : 'Unknown';
  };

  return (
    <Layout>
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-50 mb-2">
              Mark Attendance
            </h1>
            <p className="text-base text-slate-400">Use face recognition or mark manually</p>
          </div>
        </div>

        {!isFaceOnly && (
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => { stopCamera(); setMode('manual'); }}
              data-testid="mode-manual-btn"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm transition-colors ${
                mode === 'manual'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <UserPlus size={18} weight="bold" />
              Manual Attendance
            </button>
            <button
              onClick={() => setMode('face')}
              data-testid="mode-face-btn"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm transition-colors ${
                mode === 'face'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <CameraPlus size={18} weight="bold" />
              Face Recognition
            </button>
          </div>
        )}

        {mode === 'face' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-3">
                  Select Batch (Optional)
                </label>
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  data-testid="batch-filter-select"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Batches</option>
                  {batches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
                <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  {!modelsLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-950">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-slate-400">Loading face detection...</p>
                      </div>
                    </div>
                  )}

                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    width="640"
                    height="480"
                    className="w-full h-full object-cover"
                    data-testid="attendance-video"
                  />

                  <canvas
                    ref={canvasRef}
                    className="absolute top-0 left-0 w-full h-full"
                  />

                  {modelsLoaded && (
                    <>
                      <div className="scanner-beam" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="face-guide w-64 h-64" />
                      </div>
                    </>
                  )}
                </div>

                <button
                  onClick={recognizeFace}
                  disabled={!modelsLoaded || capturing}
                  data-testid="recognize-face-button"
                  className="w-full mt-4 bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <CameraPlus size={20} weight="bold" />
                  {capturing ? 'Recognizing...' : 'Recognize & Mark Attendance'}
                </button>

                {!isFaceOnly && (
                  <button
                    onClick={handleCancel}
                    data-testid="cancel-attendance-button"
                    className="w-full mt-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2"
                  >
                    <X size={20} weight="bold" />
                    Cancel & Return Home
                  </button>
                )}
              </div>
            </div>

            <div>
              {result && (
                <div
                  className={`bg-slate-900 border rounded-lg p-6 ${
                    result.success ? 'border-emerald-600' : 'border-rose-600'
                  }`}
                  data-testid="recognition-result"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        result.success ? 'bg-emerald-600/20' : 'bg-rose-600/20'
                      }`}
                    >
                      {result.success ? (
                        <CheckCircle size={28} weight="duotone" className="text-emerald-400" />
                      ) : (
                        <XCircle size={28} weight="duotone" className="text-rose-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3
                        className={`text-xl font-semibold mb-2 ${
                          result.success ? 'text-emerald-400' : 'text-rose-400'
                        }`}
                      >
                        {result.success ? 'Success!' : 'Failed'}
                      </h3>
                      <p className="text-slate-50 mb-4">{result.message}</p>
                      {result.confidence && (
                        <div className="mt-4 p-4 bg-slate-950 border border-slate-800 rounded-md">
                          <p className="text-sm text-slate-400 mb-2">Match Confidence</p>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500"
                                style={{ width: `${result.confidence}%` }}
                              />
                            </div>
                            <span className="text-lg font-bold text-emerald-400">{result.confidence}%</span>
                          </div>
                        </div>
                      )}
                      {result.student && (
                        <div className="mt-4 p-4 bg-slate-950 border border-slate-800 rounded-md">
                          <div className="flex items-center gap-4">
                            {result.student.photo_url ? (
                              <img
                                src={result.student.photo_url}
                                alt={result.student.name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-blue-500"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
                                <span className="text-2xl font-bold text-blue-400">
                                  {result.student.name.charAt(0)}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-slate-50">{result.student.name}</p>
                              <p className="text-sm text-slate-400">Age: {result.student.age}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mt-6">
                <h3 className="text-xl font-semibold text-slate-50 mb-4">Instructions</h3>
                <ol className="space-y-3 text-sm text-slate-400">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                      1
                    </span>
                    <span>Position your face within the circular guide</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                      2
                    </span>
                    <span>Ensure good lighting and remove any face coverings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                      3
                    </span>
                    <span>Click "Recognize & Mark Attendance" when ready</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                      4
                    </span>
                    <span>Wait for the system to recognize your face</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {mode === 'manual' && (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-50">Select Students to Mark Attendance</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                  Attendance Date
                </label>
                <input
                  type="date"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  data-testid="manual-date-input"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                  Filter by Batch
                </label>
                <select
                  value={manualBatch}
                  onChange={(e) => setManualBatch(e.target.value)}
                  data-testid="manual-batch-filter"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Batches</option>
                  {batches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                  Search Student
                </label>
                <input
                  type="text"
                  value={manualSearch}
                  onChange={(e) => setManualSearch(e.target.value)}
                  placeholder="Type student name..."
                  data-testid="manual-search-input"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-slate-50 placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {getManualFilteredStudents().length === 0 ? (
                <div className="text-center py-8">
                  <Users size={48} weight="duotone" className="mx-auto text-slate-700 mb-3" />
                  <p className="text-slate-500">No students found</p>
                </div>
              ) : (
                getManualFilteredStudents().map((student) => (
                  <div
                    key={student.id}
                    data-testid={`manual-student-${student.id}`}
                    className={`flex items-center justify-between p-4 border rounded-md transition-colors ${
                      absentIds.includes(student.id)
                        ? 'bg-rose-950/30 border-rose-800/40'
                        : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {student.photo_url ? (
                        <img
                          src={student.photo_url}
                          alt={student.name}
                          className="w-10 h-10 rounded-full object-cover border border-slate-700"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-400">
                            {student.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-slate-50">{student.name}</p>
                        <p className="text-xs text-slate-500">{getBatchName(student.batch_id)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {absentIds.includes(student.id) ? (
                        <button
                          onClick={() => handleRemoveAbsent(student)}
                          data-testid={`remove-absent-${student.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-600/20 text-rose-400 hover:bg-rose-600/30 rounded-md text-sm font-medium transition-colors"
                        >
                          <ProhibitInset size={16} weight="bold" />
                          Absent ({getCurrentMonth()})
                        </button>
                      ) : (
                        <>
                          <Button
                            onClick={() => handleManualAttendance(student)}
                            disabled={markingManual}
                            data-testid={`mark-manual-${student.id}`}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm px-4 py-2"
                          >
                            <CheckCircle size={16} weight="bold" className="mr-1.5" />
                            Mark Present
                          </Button>
                          <button
                            onClick={() => handleMarkAbsent(student)}
                            data-testid={`mark-absent-${student.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-800 text-slate-400 hover:bg-rose-600/20 hover:text-rose-400 rounded-md text-sm font-medium transition-colors"
                            title={`Mark absent for ${getCurrentMonth()}`}
                          >
                            <ProhibitInset size={16} weight="bold" />
                            Absent
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default MarkAttendance;
